import { BaseService } from './BaseService';
import type {
  ConnectorDto,
  ConnectorDetailDto,
  ConnectorInstanceDto,
  ConnectorInstanceDetailDto,
  ConnectorSettingsDto,
  ConnectorFilters,
  CreateConnectorInstanceRequest,
  UpdateConnectorInstanceRequest,
  UpdateConnectorSettingsRequest,
  OAuthInitiateRequest,
  OAuthInitiateResponse,
  OAuthCallbackRequest,
  OAuthCallbackResponse,
  TestConnectionRequest,
  TestConnectionResponse,
  RateLimitStatus,
  ConnectorUsageStats,
} from '../types/connector';

/**
 * Connector service for managing connectors and connector instances.
 */
class ConnectorService extends BaseService {
  /**
   * Gets a list of connectors with optional filtering.
   */
  async getConnectors(filters?: ConnectorFilters): Promise<ConnectorDto[]> {
    const queryParams = new URLSearchParams();
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

    const url = `/connectors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const connectors = await this.get<ConnectorDto[]>(url);
    
    // Apply client-side filtering for searchTerm if provided
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return connectors.filter(
        (c) =>
          c.name.toLowerCase().includes(searchLower) ||
          c.description?.toLowerCase().includes(searchLower) ||
          c.vendor?.toLowerCase().includes(searchLower)
      );
    }
    
    return connectors;
  }

  /**
   * Gets a connector by ID.
   */
  async getConnector(id: string): Promise<ConnectorDetailDto> {
    return this.get<ConnectorDetailDto>(`/connectors/${id}`);
  }

  /**
   * Gets connector instances for a connector.
   */
  async getConnectorInstances(connectorId: string, isActive?: boolean): Promise<ConnectorInstanceDto[]> {
    const queryParams = new URLSearchParams();
    if (isActive !== undefined) queryParams.append('isActive', isActive.toString());

    const url = `/connectors/${connectorId}/instances${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<ConnectorInstanceDto[]>(url);
  }

  /**
   * Gets all connector instances for the current tenant/project.
   */
  async getAllConnectorInstances(isActive?: boolean): Promise<ConnectorInstanceDto[]> {
    const queryParams = new URLSearchParams();
    if (isActive !== undefined) queryParams.append('isActive', isActive.toString());

    const url = `/connectors/instances${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<ConnectorInstanceDto[]>(url);
  }

  /**
   * Gets a connector instance by ID.
   */
  async getConnectorInstance(id: string): Promise<ConnectorInstanceDetailDto> {
    return this.get<ConnectorInstanceDetailDto>(`/connectors/instances/${id}`);
  }

  /**
   * Creates a new connector instance.
   */
  async createConnectorInstance(
    connectorId: string,
    data: CreateConnectorInstanceRequest
  ): Promise<ConnectorInstanceDetailDto> {
    return this.post<ConnectorInstanceDetailDto>(`/connectors/${connectorId}/instances`, data);
  }

  /**
   * Updates an existing connector instance.
   */
  async updateConnectorInstance(
    id: string,
    data: UpdateConnectorInstanceRequest
  ): Promise<ConnectorInstanceDetailDto> {
    return this.put<ConnectorInstanceDetailDto>(`/connectors/instances/${id}`, data);
  }

  /**
   * Deletes a connector instance.
   */
  async deleteConnectorInstance(id: string): Promise<void> {
    return this.delete<void>(`/connectors/instances/${id}`);
  }

  /**
   * Initiates OAuth flow for a connector instance.
   */
  async initiateOAuth(instanceId: string, request?: OAuthInitiateRequest): Promise<OAuthInitiateResponse> {
    return this.post<OAuthInitiateResponse>(`/connectors/instances/${instanceId}/auth/oauth/initiate`, request);
  }

  /**
   * Handles OAuth callback for a connector instance.
   */
  async handleOAuthCallback(instanceId: string, request: OAuthCallbackRequest): Promise<OAuthCallbackResponse> {
    return this.post<OAuthCallbackResponse>(`/connectors/instances/${instanceId}/auth/oauth/callback`, request);
  }

  /**
   * Tests connection for a connector instance.
   */
  async testConnection(instanceId: string, request?: TestConnectionRequest): Promise<TestConnectionResponse> {
    return this.post<TestConnectionResponse>(`/connectors/instances/${instanceId}/test-connection`, request);
  }

  /**
   * Gets settings for a connector instance.
   */
  async getConnectorSettings(instanceId: string): Promise<ConnectorSettingsDto> {
    return this.get<ConnectorSettingsDto>(`/connectors/instances/${instanceId}/settings`);
  }

  /**
   * Updates settings for a connector instance.
   */
  async updateConnectorSettings(
    instanceId: string,
    data: UpdateConnectorSettingsRequest
  ): Promise<ConnectorSettingsDto> {
    return this.put<ConnectorSettingsDto>(`/connectors/instances/${instanceId}/settings`, data);
  }

  /**
   * Gets usage statistics for a connector instance.
   */
  async getConnectorUsageStats(
    instanceId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ConnectorUsageStats> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const url = `/connectors/instances/${instanceId}/usage-stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<ConnectorUsageStats>(url);
  }

  /**
   * Gets rate limit status for a connector instance.
   */
  async getRateLimitStatus(instanceId: string): Promise<RateLimitStatus> {
    return this.get<RateLimitStatus>(`/connectors/instances/${instanceId}/rate-limit-status`);
  }
}

export const connectorService = new ConnectorService();
